import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as Popover from '@radix-ui/react-popover';
import { AnimatePresence, motion } from 'framer-motion';
import { X, CalendarDays } from 'lucide-react';
import type { PhotoPost } from '@/types';
import { REACTIONS } from '@/config/reactions';
import { formatCompactNumber, formatDate } from '@/lib/utils';
import { useReaction } from '@/hooks/useReaction';
import { FloatingReaction } from '@/components/ui/FloatingReaction';

interface PhotoLightboxProps {
  post: PhotoPost | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const PhotoLightbox = ({
  post,
  open,
  onOpenChange,
}: PhotoLightboxProps) => {
  const { react, isPending } = useReaction();

  const [trigger, setTrigger] = useState(0);
  const [emoji, setEmoji] = useState('❤️');

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && post && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild>
              <motion.div
                className="fixed inset-0 z-50 bg-black/70 backdrop-blur-xl"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              />
            </Dialog.Overlay>

            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6">
              <Dialog.Content asChild forceMount>
                <motion.div
                  className="glass relative grid max-h-[90vh] w-full max-w-6xl overflow-hidden rounded-[32px] border border-white/10 bg-zinc-950/80 shadow-2xl outline-none lg:grid-cols-[minmax(0,1.3fr)_380px]"
                  initial={{ opacity: 0, scale: 0.96, y: 12 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.98, y: 8 }}
                >
                  <div className="relative flex min-h-[320px] items-center justify-center bg-black/40">
                    <img
                      src={post.imageUrl}
                      alt={post.caption}
                      className="max-h-[90vh] w-full object-contain"
                    />
                  </div>

                  <div className="relative flex flex-col border-t border-white/10 bg-white/5 p-5 lg:border-l lg:border-t-0">
                    <FloatingReaction
                      emoji={emoji}
                      trigger={trigger}
                    />

                    <div className="flex justify-end">
                      <Dialog.Close asChild>
                        <button
                          className="rounded-full border border-white/10 bg-white/5 p-2"
                        >
                          <X size={18} />
                        </button>
                      </Dialog.Close>
                    </div>

                    <div className="mt-4 space-y-4">
                      <p className="text-white/90">
                        {post.caption}
                      </p>

                      <div className="flex items-center gap-3 text-sm text-white/60">
                        <CalendarDays size={14} />
                        {formatDate(post.createdAt)}
                      </div>

                      <div className="space-y-4 border-t border-white/10 pt-4">
                        <div className="flex gap-3">
                          {REACTIONS.map((reaction) => (
                            <motion.button
                              whileTap={{ scale: 1.35 }}
                              key={reaction.key}
                              disabled={isPending}
                              onClick={() => {
                                setEmoji(reaction.emoji);
                                setTrigger(Date.now());
                                react({ postId: post.id, reaction: reaction.key });
                              }}
                              className={`flex h-11 w-11 items-center justify-center rounded-full text-xl transition ${
                                post.userReaction === reaction.key
                                  ? 'scale-110'
                                  : 'grayscale opacity-40 hover:grayscale-0 hover:opacity-100'
                              }`}
                            >
                              {reaction.emoji}
                            </motion.button>
                          ))}
                        </div>

                        <Popover.Root>
                          <Popover.Trigger asChild>
                            <button className="text-xs text-white/45 hover:text-white transition">
                              {formatCompactNumber(post.totalReactions)} total react.
                            </button>
                          </Popover.Trigger>

                          <Popover.Portal>
                            <Popover.Content
                              side="top"
                              align="end"
                              sideOffset={8}
                              className="z-[100] w-52 rounded-2xl border border-white/10 bg-zinc-900 p-3 shadow-2xl"
                            >
                              <div className="space-y-2">
                                {REACTIONS.map((reaction) => {
                                  const count = post.reactions[reaction.key] ?? 0;
                                  if (!count) return null;

                                  return (
                                    <div
                                      key={reaction.key}
                                      className="flex items-center justify-between text-sm"
                                    >
                                      <div className="flex items-center gap-2">
                                        <span>{reaction.emoji}</span>
                                        <span>{reaction.label}</span>
                                      </div>

                                      <span>{formatCompactNumber(count)}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </Popover.Content>
                          </Popover.Portal>
                        </Popover.Root>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </Dialog.Content>
            </div>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
};